/**
 * ApiKeys - Provider API Keys Management
 * Manage encrypted API keys for voice providers
 */

import React, { useState, useEffect } from 'react';
import { 
  Key, Save, Eye, EyeOff, Loader2, CheckCircle, XCircle,
  AlertTriangle, RefreshCw, Trash2, Shield, Clock, User,
  ExternalLink, HelpCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

// Provider configuration
const PROVIDER_CONFIG = {
  openai: {
    name: 'OpenAI',
    description: 'GPT-4o Realtime API for voice conversations',
    placeholder: 'sk-...',
    docsUrl: 'https://platform.openai.com/api-keys',
    icon: '🤖',
    color: 'bg-green-100 text-green-800',
    usedBy: ['OpenAI Realtime Provider']
  },
  deepgram: {
    name: 'Deepgram',
    description: 'Nova-2 STT and Aura TTS',
    placeholder: 'Your Deepgram API key',
    docsUrl: 'https://console.deepgram.com/',
    icon: '🎙️',
    color: 'bg-purple-100 text-purple-800',
    usedBy: ['Deepgram Provider']
  },
  soniox: {
    name: 'Soniox',
    description: 'Real-time speech-to-text',
    placeholder: 'Your Soniox API key',
    docsUrl: 'https://soniox.com/',
    icon: '🗣️',
    color: 'bg-blue-100 text-blue-800',
    usedBy: ['Custom Provider', 'Intent-IVR Provider']
  },
  assemblyai: {
    name: 'AssemblyAI',
    description: 'Universal Streaming speech-to-text',
    placeholder: 'Your AssemblyAI API key',
    docsUrl: 'https://www.assemblyai.com/dashboard/api-keys',
    icon: '🧠',
    color: 'bg-indigo-100 text-indigo-800',
    usedBy: ['Custom Provider']
  },
  groq: {
    name: 'Groq',
    description: 'Fast LLM inference (GPT-OSS, Compound, Qwen)',
    placeholder: 'gsk_...',
    docsUrl: 'https://console.groq.com/keys',
    icon: '⚡',
    color: 'bg-orange-100 text-orange-800',
    usedBy: ['Custom Provider']
  },
  elevenlabs: {
    name: 'ElevenLabs',
    description: 'High-quality text-to-speech and Scribe realtime speech-to-text',
    placeholder: 'Your ElevenLabs API key',
    docsUrl: 'https://elevenlabs.io/app/settings/api-keys',
    icon: '🔊',
    color: 'bg-pink-100 text-pink-800',
    usedBy: ['Custom Provider', 'Intent-IVR Provider']
  },
  livekit_api_key: {
    name: 'LiveKit API Key',
    description: 'API key for your self-hosted LiveKit server',
    placeholder: 'API...',
    docsUrl: 'https://docs.livekit.io/home/get-started/authentication/',
    icon: '🎬',
    color: 'bg-cyan-100 text-cyan-800',
    usedBy: ['LiveKit Provider']
  },
  livekit_api_secret: {
    name: 'LiveKit API Secret',
    description: 'API secret for your self-hosted LiveKit server',
    placeholder: 'Your LiveKit API secret',
    docsUrl: 'https://docs.livekit.io/home/get-started/authentication/',
    icon: '🔐',
    color: 'bg-cyan-100 text-cyan-800',
    usedBy: ['LiveKit Provider']
  },
  livekit_url: {
    name: 'LiveKit Server URL',
    description: 'WebSocket URL of your self-hosted LiveKit server',
    placeholder: 'wss://your-livekit-server.com',
    docsUrl: 'https://docs.livekit.io/home/self-hosting/deployment/',
    icon: '🌐',
    color: 'bg-cyan-100 text-cyan-800',
    usedBy: ['LiveKit Provider']
  },
  cartesia: {
	  name: 'Cartesia',
	  description: 'High-quality neural TTS with low latency',
	  placeholder: 'Your Cartesia API key',
	  docsUrl: 'https://docs.cartesia.ai/',
	  icon: '🎵',
	  color: 'bg-indigo-100 text-indigo-800',
	  usedBy: ['LiveKit Provider (TTS)']
	},
	azure_speech_key: {
	  name: 'Azure Speech Key',
	  description: 'Azure Cognitive Services Speech API key',
	  placeholder: 'Your Azure Speech key',
	  docsUrl: 'https://azure.microsoft.com/en-us/products/ai-services/speech-services',
	  icon: '☁️',
	  color: 'bg-blue-100 text-blue-800',
	  usedBy: ['LiveKit Provider (STT/TTS)', 'Azure Speech']
	},
	azure_speech_region: {
	  name: 'Azure Speech Region',
	  description: 'Azure region for Speech Services (e.g., eastus, westus2)',
	  placeholder: 'eastus',
	  docsUrl: 'https://azure.microsoft.com/en-us/products/ai-services/speech-services',
	  icon: '🌍',
	  color: 'bg-blue-100 text-blue-800',
	  usedBy: ['LiveKit Provider (STT/TTS)', 'Azure Speech']
	},
	google_cloud_credentials: {
	  name: 'Google Cloud Credentials',
	  description: 'Google Cloud service account JSON for Speech-to-Text',
	  placeholder: '{"type": "service_account", ...}',
	  docsUrl: 'https://cloud.google.com/speech-to-text/docs/before-you-begin',
	  icon: '🔷',
	  color: 'bg-yellow-100 text-yellow-800',
	  usedBy: ['LiveKit Provider (STT)']
	},
};

export default function ApiKeys() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [keys, setKeys] = useState({});
  const [originalKeys, setOriginalKeys] = useState({});
  const [showKeys, setShowKeys] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAudit, setShowAudit] = useState(false);

  useEffect(() => {
    loadApiKeys();
  }, []);

  useEffect(() => {
    // Check for changes
    const changed = Object.keys(keys).some(provider => {
      const current = keys[provider] || '';
      const original = originalKeys[provider] || '';
      // Consider changed if value is different and not a masked value
      return current !== original && !current.includes('...');
    });
    setHasChanges(changed);
  }, [keys, originalKeys]);

  const loadApiKeys = async () => {
    try {
      setLoading(true);
      const response = await api.getApiKeys();
      const data = response.data.data;
      
      const keyValues = {};
      Object.keys(PROVIDER_CONFIG).forEach(provider => {
        keyValues[provider] = data.keys?.[provider] || '';
      });
      
      setKeys(keyValues);
      setOriginalKeys(keyValues);
    } catch (error) {
      console.error('Failed to load API keys:', error);
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const loadAuditLogs = async () => {
    try {
      const response = await api.getApiKeysAudit();
      setAuditLogs(response.data.data?.logs || []);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Only send keys that have changed and are not masked
      const keysToUpdate = {};
      Object.keys(keys).forEach(provider => {
        const current = keys[provider] || '';
        const original = originalKeys[provider] || '';
        
        // Skip if unchanged or if it's a masked value
        if (current === original || current.includes('...')) {
          return;
        }
        
        keysToUpdate[provider] = current;
      });
      
      if (Object.keys(keysToUpdate).length === 0) {
        toast.error('No changes to save');
        return;
      }
      
      await api.updateApiKeys({ keys: keysToUpdate });
      toast.success('API keys saved successfully');
      
      // Reload to get masked values
      await loadApiKeys();
    } catch (error) {
      const message = error.response?.data?.error?.message || 'Failed to save API keys';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (provider) => {
    const key = keys[provider];
    
    if (!key || key.includes('...')) {
      toast.error('Please enter a new API key to test');
      return;
    }
    
    try {
      setTesting(prev => ({ ...prev, [provider]: true }));
      const response = await api.testApiKey(provider, key);
      
      if (response.data.success) {
        toast.success(response.data.message || `${PROVIDER_CONFIG[provider].name} key is valid`);
      }
    } catch (error) {
      const message = error.response?.data?.error?.message || 'Invalid API key';
      toast.error(message);
    } finally {
      setTesting(prev => ({ ...prev, [provider]: false }));
    }
  };

  const handleDelete = async (provider) => {
    if (!window.confirm(`Are you sure you want to delete the ${PROVIDER_CONFIG[provider].name} API key?`)) {
      return;
    }
    
    try {
      await api.deleteApiKey(provider);
      toast.success(`${PROVIDER_CONFIG[provider].name} key deleted`);
      await loadApiKeys();
    } catch (error) {
      toast.error('Failed to delete API key');
    }
  };

  const toggleShowKey = (provider) => {
    setShowKeys(prev => ({ ...prev, [provider]: !prev[provider] }));
  };

  const handleKeyChange = (provider, value) => {
    setKeys(prev => ({ ...prev, [provider]: value }));
  };

  const canManageKeys = ['admin', 'super_admin'].includes(user?.role);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">API Keys</h1>
          <p className="text-gray-500">Manage your voice provider API keys securely</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setShowAudit(!showAudit);
              if (!showAudit) loadAuditLogs();
            }}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <Clock className="w-4 h-4" />
            Audit Log
          </button>
          {canManageKeys && hasChanges && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Changes
            </button>
          )}
        </div>
      </div>

      {/* Security Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
        <div>
          <h3 className="font-medium text-blue-900">Secure Storage</h3>
          <p className="text-sm text-blue-700">
            All API keys are encrypted using AES-256 encryption before being stored. 
            Keys are only decrypted when needed for API calls.
          </p>
        </div>
      </div>

      {/* API Keys Grid */}
      <div className="grid gap-6">
        {Object.entries(PROVIDER_CONFIG).map(([provider, config]) => {
          const currentKey = keys[provider] || '';
          const hasKey = currentKey && currentKey.length > 0;
          const isNewKey = hasKey && !currentKey.includes('...');
          
          return (
            <div 
              key={provider}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{config.icon}</span>
                    <div>
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        {config.name}
                        {hasKey && !isNewKey && (
                          <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                            <CheckCircle className="w-3 h-3" />
                            Configured
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-gray-500">{config.description}</p>
                    </div>
                  </div>
                  <a
                    href={config.docsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                  >
                    Get API Key
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>

                {/* Used By */}
                <div className="mb-4">
                  <span className="text-xs text-gray-500">Used by: </span>
                  {config.usedBy.map((use, idx) => (
                    <span key={use} className={`text-xs ${config.color} px-2 py-0.5 rounded-full mr-1`}>
                      {use}
                    </span>
                  ))}
                </div>

                {/* Key Input */}
                {canManageKeys ? (
                  <div className="flex gap-3">
                    <div className="relative flex-1">
                      <input
                        type={showKeys[provider] ? 'text' : 'password'}
                        value={currentKey}
                        onChange={(e) => handleKeyChange(provider, e.target.value)}
                        placeholder={config.placeholder}
                        className="w-full px-4 py-2.5 pr-20 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                      />
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                        <button
                          onClick={() => toggleShowKey(provider)}
                          className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                          title={showKeys[provider] ? 'Hide' : 'Show'}
                        >
                          {showKeys[provider] ? (
                            <EyeOff className="w-4 h-4 text-gray-500" />
                          ) : (
                            <Eye className="w-4 h-4 text-gray-500" />
                          )}
                        </button>
                        {hasKey && (
                          <button
                            onClick={() => handleDelete(provider)}
                            className="p-1.5 hover:bg-red-50 rounded-md transition-colors"
                            title="Delete key"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleTest(provider)}
                      disabled={!isNewKey || testing[provider]}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                    >
                      {testing[provider] ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      Test
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Key className="w-4 h-4" />
                    {hasKey ? 'API key configured (contact admin to modify)' : 'No API key configured'}
                  </div>
                )}

                {/* Warning for new keys */}
                {isNewKey && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-amber-600">
                    <AlertTriangle className="w-4 h-4" />
                    Unsaved changes - click "Save Changes" to apply
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Provider Requirements Info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <HelpCircle className="w-5 h-5" />
          Provider Requirements
        </h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">OpenAI Realtime Provider</h4>
            <ul className="text-gray-600 space-y-1">
              <li>• OpenAI API Key (required)</li>
            </ul>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Deepgram Provider</h4>
            <ul className="text-gray-600 space-y-1">
              <li>• Deepgram API Key (required)</li>
            </ul>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Custom Provider</h4>
            <ul className="text-gray-600 space-y-1">
              <li>• Soniox API Key (required for STT)</li>
              <li>• Groq API Key (required for LLM)</li>
              <li>• ElevenLabs API Key (optional for TTS)</li>
            </ul>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Intent-IVR Provider</h4>
            <ul className="text-gray-600 space-y-1">
              <li>• Soniox API Key (required for STT)</li>
              <li>• ElevenLabs API Key (optional for TTS)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Audit Log Modal */}
      {showAudit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">API Keys Audit Log</h2>
              <button
                onClick={() => setShowAudit(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <XCircle className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
              {auditLogs.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No audit logs found</p>
              ) : (
                <div className="space-y-3">
                  {auditLogs.map((log) => (
                    <div key={log.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className={`p-2 rounded-full ${
                        log.action === 'updated' ? 'bg-blue-100' :
                        log.action === 'deleted' ? 'bg-red-100' : 'bg-gray-100'
                      }`}>
                        {log.action === 'updated' ? (
                          <Save className="w-4 h-4 text-blue-600" />
                        ) : log.action === 'deleted' ? (
                          <Trash2 className="w-4 h-4 text-red-600" />
                        ) : (
                          <Key className="w-4 h-4 text-gray-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-900">
                          <span className="font-medium">{log.user_name || 'System'}</span>
                          {' '}{log.action}{' '}
                          <span className="font-medium">{log.key_type}</span> API key
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(log.created_at).toLocaleString()}
                          {log.ip_address && ` • ${log.ip_address}`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}